import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import { sortMediaInPlace } from "./sortLogic";
import type { MediaItem } from "./types";

// Framework-agnostic media data operations, split out of gallery.ts so
// both React and vanilla (session.ts's initial-load + 120s poll, upload.ts)
// can import directly - gallery.ts itself is dom.ts-coupled and gone as of
// this migration phase. Same split rationale as albumsApi.ts.

// Was a private helper inside albums.ts's showHomeView/showAlbumsList/
// openAlbumDetail - moved here (dom.ts-free, so route components can call
// it directly) now that navigation itself is route-driven rather than
// those three functions.
export function exitSelectMode(): void {
  if (!appState.selectMode) return;
  appState.selectMode = false;
  appState.selectedFilenames = new Set();
}

export async function loadGalleryData(): Promise<void> {
  try {
    const url = appState.currentAlbum === "all" ? "/api/media" : `/api/albums/${appState.currentAlbum}/media`;
    const res = await apiFetch(url);

    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) throw new Error("Failed to load gallery");

    const data = await res.json();
    const items: MediaItem[] = Array.isArray(data.media) ? data.media : [];

    appState.media = items;
    sortMediaInPlace();
    notifyStateChange();
  } catch (err) {
    console.error("Failed to load gallery:", err);
    appState.media = [];
    notifyStateChange();
  }
}

export async function loadFavoritesData(): Promise<void> {
  try {
    const res = await apiFetch("/api/favorites");
    if (!res.ok) return;
    const data = await res.json();
    const fav: MediaItem[] = Array.isArray(data.media) ? data.media : [];
    appState.favoritesSet = new Set(fav.map((m) => m.filename));
    notifyStateChange();
  } catch {
    // ignore
  }
}

export async function toggleFavoriteApi(filename: string): Promise<boolean> {
  const res = await apiFetch("/api/favorites/toggle", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename }),
  });

  if (res.status === 401) {
    // requestLock("unauthorized") is enough on its own - AuthGate.tsx's
    // lock handler already calls /api/logout itself on every lock, so
    // doing it here too just fired the same request twice.
    requestLock("unauthorized");
    throw new Error("unauthorized");
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || "toggle failed");
  }

  const data = await res.json();
  return !!data.favorited;
}

export async function deleteMediaApi(filename: string): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const nameEnc = encodeURIComponent(filename);
    const res = await apiFetch(`/api/media/${nameEnc}`, { method: "DELETE" });

    if (res.status === 401) {
      // See toggleFavoriteApi's 401 handler above - AuthGate.tsx's lock
      // handler already calls /api/logout on every lock, so requestLock
      // alone is enough here too.
      requestLock("unauthorized");
      return { ok: false, error: "Unauthorized" };
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: data?.error ? `Delete failed: ${data.error}` : "Delete failed." };
    }

    return { ok: true };
  } catch {
    return { ok: false, error: "Delete failed. Try again." };
  }
}
